import React from "react";
import { motion } from "framer-motion";
import {
  FormValidationSummary,
  type FormValidationError,
  useFormValidation,
} from "../common/FormValidationSummary";
import type { FormStepProps } from "../../types/application";
import { translations } from "../../i18n/translations";
import { translations as skillTranslations, skillTranslationKeys } from "../../i18n/ITCompetenciesSkills";
import { useLanguage } from "../../context/LanguageContext";
import { isMobile } from "react-device-detect";
import { useState } from "react";

// Simple extracted field indicator component
const ExtractedFieldIndicator: React.FC<{ isExtracted: boolean }> = ({
  isExtracted,
}) => {
  if (!isExtracted) return null;

  return (
    <div className="extracted-indicator">
      <svg fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
      Extracted from Resume
    </div>
  );
};

const ITCompetenciesStep: React.FC<FormStepProps> = ({
  data,
  onUpdate,
  onNext,
  onPrevious,
}) => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  const validation = useFormValidation({ t });
  const skillT =
    skillTranslations[language as keyof typeof skillTranslations] ||
    skillTranslations.en;
  const [hasUrlError, setHasUrlError] = useState(false);
  const [, setUrlErrorMessage] = useState("");
  const extractedFields = data.extractionMetadata?.extractedFields || [];
  const isValid = !hasUrlError;
  const [charCount, setCharCount] = useState(0);
  const getTranslatedSkillLabel = (value: string): string => {
    const translationKey =
      skillTranslationKeys[value as keyof typeof skillTranslationKeys];

    return translationKey ? skillT[translationKey] : value;
  };

  const isFieldExtracted = (fieldName: string): boolean => {
    return extractedFields.includes(fieldName);
  };

  // Helper function to handle skills changes
  const normalizeSkillValue = (value: string): string =>
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ");

  const getCategoryOptions = (category: string): string[] => {
    switch (category) {
      case "programmingLanguages":
        return programmingLanguages;
      case "frameworks":
        return frameworksLibraries;
      case "cloudOpsTools":
        return cloudOpsTools;
      case "dataAnalyticsTools":
        return dataAnalyticsTools;
      case "aiMlTools":
        return aiMlTools;
      case "cybersecurityTools":
        return cybersecurityTools;
      case "devOpsTools":
        return devOpsTools;
      case "versionControlTools":
        return versionControlTools;
      case "enterpriseTools":
        return enterpriseTools;
      case "projectCollabTools":
        return projectCollabTools;
      case "projectMgmtMethodologies":
        return projectMgmtMethodologies;
      default:
        return [];
    }
  };

  const isEquivalentToExistingSkill = (category: string, value: string): boolean => {
    const normalizedValue = normalizeSkillValue(value);
    if (!normalizedValue) return false;

    return getCategoryOptions(category).some((option) => {
      const normalizedOption = normalizeSkillValue(option);
      if (!normalizedOption) return false;
      return (
        normalizedOption === normalizedValue ||
        normalizedOption.includes(normalizedValue) ||
        normalizedValue.includes(normalizedOption)
      );
    });
  };

  const handleSkillsChange = (
    category: string,
    skill: string,
    isChecked: boolean
  ) => {
    const currentSkills =
      (data.enhancedData?.[
        category as keyof typeof data.enhancedData
      ] as string[]) || [];
    const otherFieldName = `${category}_other`;
    const currentOtherValue =
      (data.enhancedData?.[
        otherFieldName as keyof typeof data.enhancedData
      ] as string) || "";

    const selectedSkills = currentSkills.filter((s) => s !== "Other");
    const updatedSkills = isChecked
      ? [...new Set([...selectedSkills, skill])]
      : selectedSkills.filter((s) => s !== skill);

    const nextOtherValue =
      skill === "Other"
        ? isChecked
          ? currentOtherValue
          : ""
        : currentOtherValue &&
            isEquivalentToExistingSkill(category, currentOtherValue)
          ? ""
          : currentOtherValue;

    onUpdate({
      enhancedData: {
        ...data.enhancedData,
        [category]:
          skill === "Other"
            ? updatedSkills.filter((s) => (isChecked ? true : s !== "Other"))
            : updatedSkills,
        [otherFieldName]: nextOtherValue,
      },
    });
  };

  // Helper function to handle "Other" text input changes
  const handleOtherInputChange = (category: string, value: string) => {
    const otherFieldName = `${category}_other`;
    const currentSkills =
      (data.enhancedData?.[
        category as keyof typeof data.enhancedData
      ] as string[]) || [];

    const trimmedValue = value.trim();
    const matchedExistingSkill =
      trimmedValue && isEquivalentToExistingSkill(category, trimmedValue);

    const updatedSkills = matchedExistingSkill
      ? currentSkills.filter((s) => s !== "Other")
      : trimmedValue
        ? [...new Set([...currentSkills.filter((s) => s !== "Other"), "Other"])]
        : currentSkills.filter((s) => s !== "Other");

    onUpdate({
      enhancedData: {
        ...data.enhancedData,
        [category]: updatedSkills,
        [otherFieldName]: value,
      },
    });
  };

  // Helper function to check if "Other" is selected for a category
  const isOtherSelected = (category: string): boolean => {
    const currentSkills =
      (data.enhancedData?.[
        category as keyof typeof data.enhancedData
      ] as string[]) || [];
    const otherValue =
      (data.enhancedData?.[
        `${category}_other` as keyof typeof data.enhancedData
      ] as string) || "";

    return (
      currentSkills.includes("Other") ||
      (otherValue.trim().length > 0 &&
        !isEquivalentToExistingSkill(category, otherValue))
    );
  };
  const detectUrls = (text: string): boolean => {
    // Simple regex patterns to detect URLs
    const urlPatterns = [
      /https?:\/\/[^\s]+/gi, // http:// or https://
      /www\d*\.[^\s]+/gi, // www, www2, www3, etc. followed by domain
      /(?:^|\s)([a-zA-Z0-9-]+\.(?:com|org|net|edu|gov|mil|int|co|uk|ca|de|fr|au|jp|cn|ru|br|in|io|ly|me|tv|biz|info|name|pro|aero|museum|coop)\b)/gi, // Common TLDs only
    ];

    return urlPatterns.some((pattern) => pattern.test(text));
  };
  const validateAdditionalInfo = (text: string) => {
    if (detectUrls(text)) {
      setHasUrlError(true);
      setUrlErrorMessage(t.formValidationUrlMessage);
    } else {
      setHasUrlError(false);
      setUrlErrorMessage("");
    }
  };

  // Question 27: Programming Languages
  const programmingLanguages = [
    "C#",
    "C++",
    "Go",
    "Java",
    "JavaScript",
    "Python",
    "R",
    "SQL",
    "TypeScript",
    "Other",
  ];

  // Question 28: Frameworks and Libraries - using backend values
  const frameworksLibraries = [
    ".NET",
    "Angular",
    "Node.js",
    "PyTorch",
    "React",
    "Scikit-learn",
    "Spring Boot",
    "TensorFlow",
    "Other",
  ];

  // Question 29: Cloud Operations and Tools
  const cloudOpsTools = [
    "Ansible",
    "AWS",
    "Azure",
    "Cloud Formation",
    "Docker",
    "GCP",
    "Kubernetes",
    "Terraform",
    "Other",
  ];

  // Question 30: Data & Analytics Tools
  const dataAnalyticsTools = [
    "Data Engineering",
    "Data Warehousing",
    "Databricks",
    "ETL",
    "Excel",
    "Looker",
    "Pipelines",
    "Power Query",
    "Power BI",
    "Snowflake",
    "SSIS / SSRS",
    "Tableau",
    "Other",
  ];

  // Question 31: AI/ML, Data Science, and Cloud ML Tools
  const aiMlTools = [
    "AWS SageMaker",
    "Azure Machine Learning",
    "Django",
    "Docker",
    "Flask",
    "Google Cloud AI Platform",
    "Hugging Face Transformers",
    "IBM Watson",
    "Jupyter Notebooks",
    "Keras",
    "Kubernetes",
    "LightGBM",
    "Microsoft Copilot",
    "MLflow",
    "NoSQL (MongoDB, etc.)",
    "PowerBI",
    "PyTorch",
    "Scikit-learn",
    "SQL (PostgreSQL, MySQL, etc.)",
    "Tableau",
    "TensorFlow",
    "XGBoost",
    "Other",
  ];

  // Question 32: Cybersecurity Areas and Tools
  const cybersecurityTools = [
    "1 Password MSP Edition",
    "ArmyG6-CA Cyber Concept",
    "Burp Suite",
    "Canadian Cyber Security Tool (CCST)",
    "Chaîne de Montage",
    "Check Point Quantum",
    "Cloud Security",
    "CNR Virtuel pour Azure (MapleTap)",
    "CrowdStrike Falcon",
    "IAM (Identity & Access Management)",
    "Metasploit",
    "Penetration Testing",
    "Pont d'Observation",
    "SIEM",
    "SOC (Security Operations Center)",
    "Splunk",
    "Target Security Operating Model (TSOM)",
    "Wireshark",
    "Zero Trust Architecture",
    "Other",
  ];

  // Question 33: DevOps Tools
  const devOpsTools = [
    "Argo CD",
    "Azure DevOps",
    "Bamboo",
    "CircleCI",
    "Docker",
    "GitHub Actions",
    "GitLab CI/CD",
    "Jenkins",
    "Nexus Repository",
    "OpenShift",
    "Pulumi",
    "SonarQube",
    "Spinnaker",
    "Terraform",
    "Travis CI",
    "Other",
  ];

  // Question 34: Version Control Tools
  const versionControlTools = [
    "Azure Repos",
    "Bitbucket",
    "Git",
    "GitHub",
    "GitLab",
    "Mercurial",
    "Perforce (Helix Core)",
    "Rational ClearCase",
    "Other",
  ];

  // Question 35: Enterprise Tools & Platforms
  const enterpriseTools = [
    "Informatica",
    "M365 (Microsoft 365)",
    "Oracle",
    "Peoplesoft",
    "Salesforce",
    "SAP",
    "ServiceNow",
    "SharePoint",
    "Teams",
    "Other",
  ];

  // Question 36: Project & Collaboration Tools
  const projectCollabTools = [
    "Azure DevOps",
    "Confluence",
    "D365 (Dynamics 365)",
    "GCdocs",
    "GCpedia / GCconnex / GCcollab",
    "GitHub Projects",
    "Jira",
    "Microsoft Teams",
    "MS Project",
    "SharePoint",
    "Trello",
    "Other",
  ];

  // Question 37: Project Management Methodologies
  const projectMgmtMethodologies = [
    "Agile-Waterfall Hybrid",
    "Critical Path Method (CPM)",
    "Crystal",
    "Disciplined Agile Delivery (DAD)",
    "Dynamic Systems Development Method (DSDM)",
    "Extreme Programming (XP)",
    "Integrated Project Delivery (IPD)",
    "Kanban",
    "Lean",
    "Lean Six Sigma",
    "LeSS (Large Scale Scrum)",
    "Nexus",
    "PMBOK (Project Management Body of Knowledge)",
    "PRINCE2",
    "PRINCE2 Agile",
    "Program Evaluation and Review Technique (PERT)",
    "SAFe (Scaled Agile Framework)",
    "Scrum",
    "SixSigma",
    "Spotify Model",
    "Waterfall",
    "Other",
  ];

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    validation.handleSubmit(e, onNext, getValidationErrors());
  };

  const getValidationErrors = (): FormValidationError[] => {
    if (isValid) return [];

    return [
      {
        id: "additionalInformation",
        label: `38. {t.q40}`,
        message: t.formValidationUrlMessage,
      },
    ];
  };

  if (isMobile)
    return (
      <motion.form
        ref={validation.formRef}
        onSubmit={handleSubmit}
        onChangeCapture={validation.handleChangeCapture}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
        className="wb-frmvld space-y-8"
        noValidate
      >
        <FormValidationSummary
          t={t}
          errors={validation.errors}
          errorSummaryRef={validation.errorSummaryRef}
          formRef={validation.formRef}
        />
        <div className="text-left">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t.statusSkillsCompetencies}
          </h2>
        </div>

        {/* Question 27: Programming Languages */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            27. {t.q27}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">27. {t.q27}</legend>
            <ul className="list-unstyled lst-spcd-2">
              {programmingLanguages.map((language) => (
                <li key={language} className="checkbox">
                  <input
                    type="checkbox"
                    id={`programmingLanguages-${language.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData?.programmingLanguages as string[]) ||
                      []
                    ).includes(language)}
                    onChange={(e) =>
                      handleSkillsChange(
                        "programmingLanguages",
                        language,
                        e.target.checked
                      )
                    }
                  />
                  <label
                    htmlFor={`programmingLanguages-${language.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(language)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other programming languages input */}
          {isOtherSelected("programmingLanguages") && (
            <div className="mt-4">
              <label
                htmlFor="programmingLanguagesOther"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q27other}
              </label>
              <input
                type="text"
                id="programmingLanguagesOther"
                className="w-full border border-black p-3 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., Ruby, Kotlin, Swift..."
                value={data.enhancedData?.programmingLanguages_other || ""}
                onChange={(e) =>
                  handleOtherInputChange("programmingLanguages", e.target.value)
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("programmingLanguages")}
          />
        </div>

        {/* Question 28: Frameworks and Libraries */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            28.{t.q28}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">28. {t.q28}</legend>
            <ul className="list-unstyled lst-spcd-2">
              {frameworksLibraries.map((framework) => (
                <li key={framework} className="checkbox">
                  <input
                    type="checkbox"
                    id={`frameworks-${framework.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData?.frameworks as string[]) || []
                    ).includes(framework)}
                    onChange={(e) =>
                      handleSkillsChange(
                        "frameworks",
                        framework,
                        e.target.checked
                      )
                    }
                  />
                  <label
                    htmlFor={`frameworks-${framework.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(framework)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other frameworks input */}
          {isOtherSelected("frameworks") && (
            <div className="mt-4">
              <label
                htmlFor="frameworksOther"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q28other}
              </label>
              <input
                type="text"
                id="frameworksOther"
                className="w-full border border-black p-3 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., Laravel, Express.js, Flask..."
                value={data.enhancedData?.frameworks_other || ""}
                onChange={(e) =>
                  handleOtherInputChange("frameworks", e.target.value)
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("frameworks")}
          />
        </div>

        {/* Question 29: Cloud Operations and Tools */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            29. {t.q29}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">29. {t.q29}</legend>
            <ul className="list-unstyled lst-spcd-2">
              {cloudOpsTools.map((tool) => (
                <li key={tool} className="checkbox">
                  <input
                    type="checkbox"
                    id={`cloudOpsTools-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData?.cloudOpsTools as string[]) || []
                    ).includes(tool)}
                    onChange={(e) =>
                      handleSkillsChange(
                        "cloudOpsTools",
                        tool,
                        e.target.checked
                      )
                    }
                  />
                  <label
                    htmlFor={`cloudOpsTools-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(tool)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other cloud ops tools input */}
          {isOtherSelected("cloudOpsTools") && (
            <div className="mt-4">
              <label
                htmlFor="cloudOpsToolsOther"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q29other}
              </label>
              <input
                type="text"
                id="cloudOpsToolsOther"
                className="w-full border border-black p-3 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., Digital Ocean, Heroku, Oracle Cloud..."
                value={data.enhancedData?.cloudOpsTools_other || ""}
                onChange={(e) =>
                  handleOtherInputChange("cloudOpsTools", e.target.value)
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("cloudOpsTools")}
          />
        </div>

        {/* Question 30: Data & Analytics Tools */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            30.{t.q30}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">30. {t.q30}</legend>
            <ul className="list-unstyled lst-spcd-2">
              {dataAnalyticsTools.map((tool) => (
                <li key={tool} className="checkbox">
                  <input
                    type="checkbox"
                    id={`dataAnalyticsTools-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData?.dataAnalyticsTools as string[]) || []
                    ).includes(tool)}
                    onChange={(e) =>
                      handleSkillsChange(
                        "dataAnalyticsTools",
                        tool,
                        e.target.checked
                      )
                    }
                  />
                  <label
                    htmlFor={`dataAnalyticsTools-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(tool)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other data analytics tools input */}
          {isOtherSelected("dataAnalyticsTools") && (
            <div className="mt-4">
              <label
                htmlFor="dataAnalyticsToolsOther"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q30other}
              </label>
              <input
                type="text"
                id="dataAnalyticsToolsOther"
                className="w-full border border-black p-3 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., Qlik, SAS, R Studio..."
                value={data.enhancedData?.dataAnalyticsTools_other || ""}
                onChange={(e) =>
                  handleOtherInputChange("dataAnalyticsTools", e.target.value)
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("dataAnalyticsTools")}
          />
        </div>

        {/* Question 31: AI/ML, Data Science, and Cloud ML Tools */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            31. {t.q31}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">31. {t.q31}</legend>
            <ul className="list-unstyled lst-spcd-2">
              {aiMlTools.map((tool) => (
                <li key={tool} className="checkbox">
                  <input
                    type="checkbox"
                    id={`aiMlTools-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData?.aiMlTools as string[]) || []
                    ).includes(tool)}
                    onChange={(e) =>
                      handleSkillsChange("aiMlTools", tool, e.target.checked)
                    }
                  />
                  <label
                    htmlFor={`aiMlTools-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(tool)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other AI/ML tools input */}
          {isOtherSelected("aiMlTools") && (
            <div className="mt-4">
              <label
                htmlFor="aiMlToolsOther"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q31other}
              </label>
              <input
                type="text"
                id="aiMlToolsOther"
                className="w-full border border-black p-3 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., Databricks, Apache Spark, RAPIDS..."
                value={data.enhancedData?.aiMlTools_other || ""}
                onChange={(e) =>
                  handleOtherInputChange("aiMlTools", e.target.value)
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("aiMlTools")}
          />
        </div>

        {/* Question 32: Cybersecurity Areas and Tools */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            32. {t.q32}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">32. {t.q32}</legend>
            <ul className="list-unstyled lst-spcd-2">
              {cybersecurityTools.map((tool) => (
                <li key={tool} className="checkbox">
                  <input
                    type="checkbox"
                    id={`cybersecurityTools-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData?.cybersecurityTools as string[]) || []
                    ).includes(tool)}
                    onChange={(e) =>
                      handleSkillsChange(
                        "cybersecurityTools",
                        tool,
                        e.target.checked
                      )
                    }
                  />
                  <label
                    htmlFor={`cybersecurityTools-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(tool)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other cybersecurity tools input */}
          {isOtherSelected("cybersecurityTools") && (
            <div className="mt-4">
              <label
                htmlFor="cybersecurityToolsOther"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q32other}
              </label>
              <input
                type="text"
                id="cybersecurityToolsOther"
                className="w-full border border-black p-3 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., Nessus, Qualys, FireEye..."
                value={data.enhancedData?.cybersecurityTools_other || ""}
                onChange={(e) =>
                  handleOtherInputChange("cybersecurityTools", e.target.value)
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("cybersecurityTools")}
          />
        </div>

        {/* Question 33: DevOps Tools */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            33. {t.q33}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">33. {t.q33}</legend>
            <ul className="list-unstyled lst-spcd-2">
              {devOpsTools.map((tool) => (
                <li key={tool} className="checkbox">
                  <input
                    type="checkbox"
                    id={`devOpsTools-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData?.devOpsTools as string[]) || []
                    ).includes(tool)}
                    onChange={(e) =>
                      handleSkillsChange("devOpsTools", tool, e.target.checked)
                    }
                  />
                  <label
                    htmlFor={`devOpsTools-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(tool)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other DevOps tools input */}
          {isOtherSelected("devOpsTools") && (
            <div className="mt-4">
              <label
                htmlFor="devOpsToolsOther"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q33other}
              </label>
              <input
                type="text"
                id="devOpsToolsOther"
                className="w-full border border-black p-3 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., Ansible, Chef, Puppet..."
                value={data.enhancedData?.devOpsTools_other || ""}
                onChange={(e) =>
                  handleOtherInputChange("devOpsTools", e.target.value)
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("devOpsTools")}
          />
        </div>

        {/* Question 34: Version Control Tools */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            34. {t.q34}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">34. {t.q34}</legend>
            <ul className="list-unstyled lst-spcd-2">
              {versionControlTools.map((tool) => (
                <li key={tool} className="checkbox">
                  <input
                    type="checkbox"
                    id={`versionControlTools-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData?.versionControlTools as string[]) || []
                    ).includes(tool)}
                    onChange={(e) =>
                      handleSkillsChange(
                        "versionControlTools",
                        tool,
                        e.target.checked
                      )
                    }
                  />
                  <label
                    htmlFor={`versionControlTools-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(tool)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other version control tools input */}
          {isOtherSelected("versionControlTools") && (
            <div className="mt-4">
              <label
                htmlFor="versionControlToolsOther"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q34other}
              </label>
              <input
                type="text"
                id="versionControlToolsOther"
                className="w-full border border-black p-3 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., Subversion (SVN), Bazaar..."
                value={data.enhancedData?.versionControlTools_other || ""}
                onChange={(e) =>
                  handleOtherInputChange("versionControlTools", e.target.value)
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("versionControlTools")}
          />
        </div>

        {/* Question 35: Enterprise Tools & Platforms */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            35. {t.q35}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">35. {t.q35}</legend>
            <ul className="list-unstyled lst-spcd-2">
              {enterpriseTools.map((tool) => (
                <li key={tool} className="checkbox">
                  <input
                    type="checkbox"
                    id={`enterpriseTools-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData?.enterpriseTools as string[]) || []
                    ).includes(tool)}
                    onChange={(e) =>
                      handleSkillsChange(
                        "enterpriseTools",
                        tool,
                        e.target.checked
                      )
                    }
                  />
                  <label
                    htmlFor={`enterpriseTools-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(tool)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other enterprise tools input */}
          {isOtherSelected("enterpriseTools") && (
            <div className="mt-4">
              <label
                htmlFor="enterpriseToolsOther"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q35other}
              </label>
              <input
                type="text"
                id="enterpriseToolsOther"
                className="w-full border border-black p-3 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., Workday, NetSuite, HubSpot..."
                value={data.enhancedData?.enterpriseTools_other || ""}
                onChange={(e) =>
                  handleOtherInputChange("enterpriseTools", e.target.value)
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("enterpriseTools")}
          />
        </div>

        {/* Question 36: Project & Collaboration Tools */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            36. {t.q36}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">36. {t.q36}</legend>
            <ul className="list-unstyled lst-spcd-2">
              {projectCollabTools.map((tool) => (
                <li key={tool} className="checkbox">
                  <input
                    type="checkbox"
                    id={`projectCollabTools-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData?.projectCollabTools as string[]) || []
                    ).includes(tool)}
                    onChange={(e) =>
                      handleSkillsChange(
                        "projectCollabTools",
                        tool,
                        e.target.checked
                      )
                    }
                  />
                  <label
                    htmlFor={`projectCollabTools-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(tool)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other project collaboration tools input */}
          {isOtherSelected("projectCollabTools") && (
            <div className="mt-4">
              <label
                htmlFor="projectCollabToolsOther"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q36other}
              </label>
              <input
                type="text"
                id="projectCollabToolsOther"
                className="w-full border border-black p-3 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., Slack, Asana, Monday.com..."
                value={data.enhancedData?.projectCollabTools_other || ""}
                onChange={(e) =>
                  handleOtherInputChange("projectCollabTools", e.target.value)
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("projectCollabTools")}
          />
        </div>

        {/* Question 37: Project Management Methodologies */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            37. {t.q37}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">37. {t.q37}</legend>
            <ul className="list-unstyled lst-spcd-2">
              {projectMgmtMethodologies.map((methodology) => (
                <li key={methodology} className="checkbox">
                  <input
                    type="checkbox"
                    id={`projectMgmtMethodologies-${methodology.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData
                        ?.projectMgmtMethodologies as string[]) || []
                    ).includes(methodology)}
                    onChange={(e) =>
                      handleSkillsChange(
                        "projectMgmtMethodologies",
                        methodology,
                        e.target.checked
                      )
                    }
                  />
                  <label
                    htmlFor={`projectMgmtMethodologies-${methodology.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(methodology)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other project management methodologies input */}
          {isOtherSelected("projectMgmtMethodologies") && (
            <div className="mt-4">
              <label
                htmlFor="projectMgmtMethodologiesOther"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q37other}
              </label>
              <input
                type="text"
                id="projectMgmtMethodologiesOther"
                className="w-full border border-black p-3 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., DevOps, Design Thinking, Feature-Driven Development..."
                value={data.enhancedData?.projectMgmtMethodologies_other || ""}
                onChange={(e) =>
                  handleOtherInputChange(
                    "projectMgmtMethodologies",
                    e.target.value
                  )
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("projectMgmtMethodologies")}
          />
        </div>

        {/* Questions 38 and so on: Resume-related questions */}
        <div>
          {/* Question 38: Additional Information */}
          <div>
            <label
              htmlFor="additionalInformation"
              className="block text-lg font-semibold text-gray-900 mb-2"
            >
              38. {t.q40}
            </label>
            <p className="text-lg text-gray-900 mb-3">{t.additionalInfoDesc}</p>
            <textarea
              id="additionalInformation"
              rows={4}
              maxLength={500}
              className={`w-full border p-3 focus:ring-2 focus:ring-primary-500 ${
                hasUrlError
                  ? "border-red-500 focus:border-red-500 bg-red-50"
                  : charCount >= 450
                    ? "border-yellow-500 focus:border-yellow-500 bg-yellow-50"
                    : "border-black focus:border-primary-500"
              }`}
              placeholder={t.q38Placeholder}
              value={data.enhancedData?.additionalInformation || ""}
              onChange={(e) => {
                const text = e.target.value;
                e.currentTarget.setCustomValidity(
                  detectUrls(text) ? t.formValidationUrlMessage : ""
                );
                setCharCount(text.length);
                if (text.length <= 500) {
                  onUpdate({
                    enhancedData: {
                      ...data.enhancedData,
                      additionalInformation: text,
                    },
                  });
                }
                validateAdditionalInfo(text);
              }}
            />
            {/* Character counter */}
            <div className="flex justify-between mt-1">
              <div>
                {hasUrlError && (
                  <p className="text-xs text-red-600">
                    {t.formValidationUrlMessage}
                  </p>
                )}
              </div>
              <div
                className={`text-xs ${
                  charCount >= 450
                    ? charCount >= 480
                      ? "text-red-600"
                      : "text-yellow-600"
                    : "text-gray-500"
                }`}
              >
                {charCount}/500
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onPrevious}
              className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t.previousButton}
            </button>
            <button
              type="submit"
              className="w-full sm:w-auto px-6 py-3 rounded-lg font-medium transition-colors primary-action-button"
            >
              {t.continueReview}
            </button>
          </div>
        </div>
      </motion.form>
    );
  else
    return (
      <motion.form
        ref={validation.formRef}
        onSubmit={handleSubmit}
        onChangeCapture={validation.handleChangeCapture}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
        className="wb-frmvld space-y-8"
        noValidate
      >
        <FormValidationSummary
          t={t}
          errors={validation.errors}
          errorSummaryRef={validation.errorSummaryRef}
          formRef={validation.formRef}
        />
        <div className="space-y-8">
        {/* Question 27: Programming Languages */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            27. {t.q27}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">27. {t.q27}</legend>
            <ul className="list-unstyled lst-spcd-2 colcount-md-2 colcount-sm-2">
              {programmingLanguages.map((language) => (
                <li
                  key={language}
                  className="checkbox"
                  style={{
                    breakInside: "avoid",
                    pageBreakInside: "avoid",
                    fontSize: "0.875rem",
                    verticalAlign: "top",
                  }}
                >
                  <input
                    type="checkbox"
                    id={`programmingLanguages-desktop-${language.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData?.programmingLanguages as string[]) ||
                      []
                    ).includes(language)}
                    onChange={(e) =>
                      handleSkillsChange(
                        "programmingLanguages",
                        language,
                        e.target.checked
                      )
                    }
                  />
                  <label
                    htmlFor={`programmingLanguages-desktop-${language.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(language)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other programming languages input */}
          {isOtherSelected("programmingLanguages") && (
            <div className="mt-4">
              <label
                htmlFor="programmingLanguagesOther-desktop"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q27other}
              </label>
              <input
                type="text"
                id="programmingLanguagesOther-desktop"
                className="w-full border border-gray-950 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Ruby, Kotlin, Swift..."
                value={data.enhancedData?.programmingLanguages_other || ""}
                onChange={(e) =>
                  handleOtherInputChange("programmingLanguages", e.target.value)
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("programmingLanguages")}
          />
        </div>

        {/* Question 28: Frameworks and Libraries */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            28. {t.q28}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">28. {t.q28}</legend>
            <ul className="list-unstyled lst-spcd-2 colcount-md-2 colcount-sm-2">
              {frameworksLibraries.map((framework) => (
                <li
                  key={framework}
                  className="checkbox"
                  style={{
                    breakInside: "avoid",
                    pageBreakInside: "avoid",
                    fontSize: "0.875rem",
                    verticalAlign: "top",
                  }}
                >
                  <input
                    type="checkbox"
                    id={`frameworks-desktop-${framework.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData?.frameworks as string[]) || []
                    ).includes(framework)}
                    onChange={(e) =>
                      handleSkillsChange(
                        "frameworks",
                        framework,
                        e.target.checked
                      )
                    }
                  />
                  <label
                    htmlFor={`frameworks-desktop-${framework.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(framework)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other frameworks input */}
          {isOtherSelected("frameworks") && (
            <div className="mt-4">
              <label
                htmlFor="frameworksOther-desktop"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q28other}
              </label>
              <input
                type="text"
                id="frameworksOther-desktop"
                className="w-full border border-gray-950 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Laravel, Express.js, Flask..."
                value={data.enhancedData?.frameworks_other || ""}
                onChange={(e) =>
                  handleOtherInputChange("frameworks", e.target.value)
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("frameworks")}
          />
        </div>

        {/* Question 29: Cloud Operations and Tools */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            29. {t.q29}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">29. {t.q29}</legend>
            <ul className="list-unstyled lst-spcd-2 colcount-md-2 colcount-sm-2">
              {cloudOpsTools.map((tool) => (
                <li
                  key={tool}
                  className="checkbox"
                  style={{
                    breakInside: "avoid",
                    pageBreakInside: "avoid",
                    fontSize: "0.875rem",
                    verticalAlign: "top",
                  }}
                >
                  <input
                    type="checkbox"
                    id={`cloudOpsTools-desktop-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData?.cloudOpsTools as string[]) || []
                    ).includes(tool)}
                    onChange={(e) =>
                      handleSkillsChange(
                        "cloudOpsTools",
                        tool,
                        e.target.checked
                      )
                    }
                  />
                  <label
                    htmlFor={`cloudOpsTools-desktop-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(tool)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other cloud ops tools input */}
          {isOtherSelected("cloudOpsTools") && (
            <div className="mt-4">
              <label
                htmlFor="cloudOpsToolsOther-desktop"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q29other}
              </label>
              <input
                type="text"
                id="cloudOpsToolsOther-desktop"
                className="w-full border border-gray-950 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Digital Ocean, Heroku, Oracle Cloud..."
                value={data.enhancedData?.cloudOpsTools_other || ""}
                onChange={(e) =>
                  handleOtherInputChange("cloudOpsTools", e.target.value)
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("cloudOpsTools")}
          />
        </div>

        {/* Question 30: Data & Analytics Tools */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            30.{t.q30}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">30. {t.q30}</legend>
            <ul className="list-unstyled lst-spcd-2 colcount-md-2 colcount-sm-2">
              {dataAnalyticsTools.map((tool) => (
                <li
                  key={tool}
                  className="checkbox"
                  style={{
                    breakInside: "avoid",
                    pageBreakInside: "avoid",
                    fontSize: "0.875rem",
                    verticalAlign: "top",
                  }}
                >
                  <input
                    type="checkbox"
                    id={`dataAnalyticsTools-desktop-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData?.dataAnalyticsTools as string[]) || []
                    ).includes(tool)}
                    onChange={(e) =>
                      handleSkillsChange(
                        "dataAnalyticsTools",
                        tool,
                        e.target.checked
                      )
                    }
                  />
                  <label
                    htmlFor={`dataAnalyticsTools-desktop-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(tool)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other data analytics tools input */}
          {isOtherSelected("dataAnalyticsTools") && (
            <div className="mt-4">
              <label
                htmlFor="dataAnalyticsToolsOther-desktop"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q30other}
              </label>
              <input
                type="text"
                id="dataAnalyticsToolsOther-desktop"
                className="w-full border border-gray-950 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Qlik, SAS, R Studio..."
                value={data.enhancedData?.dataAnalyticsTools_other || ""}
                onChange={(e) =>
                  handleOtherInputChange("dataAnalyticsTools", e.target.value)
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("dataAnalyticsTools")}
          />
        </div>

        {/* Question 31: AI/ML, Data Science, and Cloud ML Tools */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            31. {t.q31}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">31. {t.q31}</legend>
            <ul className="list-unstyled lst-spcd-2 colcount-md-2 colcount-sm-2">
              {aiMlTools.map((tool) => (
                <li
                  key={tool}
                  className="checkbox"
                  style={{
                    breakInside: "avoid",
                    pageBreakInside: "avoid",
                    fontSize: "0.875rem",
                    verticalAlign: "top",
                  }}
                >
                  <input
                    type="checkbox"
                    id={`aiMlTools-desktop-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData?.aiMlTools as string[]) || []
                    ).includes(tool)}
                    onChange={(e) =>
                      handleSkillsChange("aiMlTools", tool, e.target.checked)
                    }
                  />
                  <label
                    htmlFor={`aiMlTools-desktop-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(tool)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other AI/ML tools input */}
          {isOtherSelected("aiMlTools") && (
            <div className="mt-4">
              <label
                htmlFor="aiMlToolsOther-desktop"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q31other}
              </label>
              <input
                type="text"
                id="aiMlToolsOther-desktop"
                className="w-full border border-gray-950 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Databricks, Apache Spark, RAPIDS..."
                value={data.enhancedData?.aiMlTools_other || ""}
                onChange={(e) =>
                  handleOtherInputChange("aiMlTools", e.target.value)
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("aiMlTools")}
          />
        </div>

        {/* Question 32: Cybersecurity Areas and Tools */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            32. {t.q32}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">32. {t.q32}</legend>
            <ul className="list-unstyled lst-spcd-2 colcount-md-2 colcount-sm-2">
              {cybersecurityTools.map((tool) => (
                <li
                  key={tool}
                  className="checkbox"
                  style={{
                    breakInside: "avoid",
                    pageBreakInside: "avoid",
                    fontSize: "0.875rem",
                    verticalAlign: "top",
                  }}
                >
                  <input
                    type="checkbox"
                    id={`cybersecurityTools-desktop-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData?.cybersecurityTools as string[]) || []
                    ).includes(tool)}
                    onChange={(e) =>
                      handleSkillsChange(
                        "cybersecurityTools",
                        tool,
                        e.target.checked
                      )
                    }
                  />
                  <label
                    htmlFor={`cybersecurityTools-desktop-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(tool)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other cybersecurity tools input */}
          {isOtherSelected("cybersecurityTools") && (
            <div className="mt-4">
              <label
                htmlFor="cybersecurityToolsOther-desktop"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q32other}
              </label>
              <input
                type="text"
                id="cybersecurityToolsOther-desktop"
                className="w-full border border-gray-950 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Nessus, Qualys, FireEye..."
                value={data.enhancedData?.cybersecurityTools_other || ""}
                onChange={(e) =>
                  handleOtherInputChange("cybersecurityTools", e.target.value)
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("cybersecurityTools")}
          />
        </div>

        {/* Question 33: DevOps Tools */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            33. {t.q33}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">33. {t.q33}</legend>
            <ul className="list-unstyled lst-spcd-2 colcount-md-2 colcount-sm-2">
              {devOpsTools.map((tool) => (
                <li
                  key={tool}
                  className="checkbox"
                  style={{
                    breakInside: "avoid",
                    pageBreakInside: "avoid",
                    fontSize: "0.875rem",
                    verticalAlign: "top",
                  }}
                >
                  <input
                    type="checkbox"
                    id={`devOpsTools-desktop-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData?.devOpsTools as string[]) || []
                    ).includes(tool)}
                    onChange={(e) =>
                      handleSkillsChange("devOpsTools", tool, e.target.checked)
                    }
                  />
                  <label
                    htmlFor={`devOpsTools-desktop-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(tool)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other DevOps tools input */}
          {isOtherSelected("devOpsTools") && (
            <div className="mt-4">
              <label
                htmlFor="devOpsToolsOther-desktop"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q33other}
              </label>
              <input
                type="text"
                id="devOpsToolsOther-desktop"
                className="w-full border border-gray-950 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Ansible, Chef, Puppet..."
                value={data.enhancedData?.devOpsTools_other || ""}
                onChange={(e) =>
                  handleOtherInputChange("devOpsTools", e.target.value)
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("devOpsTools")}
          />
        </div>

        {/* Question 34: Version Control Tools */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            34. {t.q34}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">34. {t.q34}</legend>
            <ul className="list-unstyled lst-spcd-2 colcount-md-2 colcount-sm-2">
              {versionControlTools.map((tool) => (
                <li
                  key={tool}
                  className="checkbox"
                  style={{
                    breakInside: "avoid",
                    pageBreakInside: "avoid",
                    fontSize: "0.875rem",
                    verticalAlign: "top",
                  }}
                >
                  <input
                    type="checkbox"
                    id={`versionControlTools-desktop-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData?.versionControlTools as string[]) || []
                    ).includes(tool)}
                    onChange={(e) =>
                      handleSkillsChange(
                        "versionControlTools",
                        tool,
                        e.target.checked
                      )
                    }
                  />
                  <label
                    htmlFor={`versionControlTools-desktop-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(tool)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other version control tools input */}
          {isOtherSelected("versionControlTools") && (
            <div className="mt-4">
              <label
                htmlFor="versionControlToolsOther-desktop"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q34other}
              </label>
              <input
                type="text"
                id="versionControlToolsOther-desktop"
                className="w-full border border-gray-950 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Subversion (SVN), Bazaar..."
                value={data.enhancedData?.versionControlTools_other || ""}
                onChange={(e) =>
                  handleOtherInputChange("versionControlTools", e.target.value)
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("versionControlTools")}
          />
        </div>

        {/* Question 35: Enterprise Tools & Platforms */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            35. {t.q35}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">35. {t.q35}</legend>
            <ul className="list-unstyled lst-spcd-2 colcount-md-2 colcount-sm-2">
              {enterpriseTools.map((tool) => (
                <li
                  key={tool}
                  className="checkbox"
                  style={{
                    breakInside: "avoid",
                    pageBreakInside: "avoid",
                    fontSize: "0.875rem",
                    verticalAlign: "top",
                  }}
                >
                  <input
                    type="checkbox"
                    id={`enterpriseTools-desktop-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData?.enterpriseTools as string[]) || []
                    ).includes(tool)}
                    onChange={(e) =>
                      handleSkillsChange(
                        "enterpriseTools",
                        tool,
                        e.target.checked
                      )
                    }
                  />
                  <label
                    htmlFor={`enterpriseTools-desktop-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(tool)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other enterprise tools input */}
          {isOtherSelected("enterpriseTools") && (
            <div className="mt-4">
              <label
                htmlFor="enterpriseToolsOther-desktop"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q35other}
              </label>
              <input
                type="text"
                id="enterpriseToolsOther-desktop"
                className="w-full border border-gray-950 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Workday, NetSuite, HubSpot..."
                value={data.enhancedData?.enterpriseTools_other || ""}
                onChange={(e) =>
                  handleOtherInputChange("enterpriseTools", e.target.value)
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("enterpriseTools")}
          />
        </div>

        {/* Question 36: Project & Collaboration Tools */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            36. {t.q36}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">36. {t.q36}</legend>
            <ul className="list-unstyled lst-spcd-2 colcount-md-2 colcount-sm-2">
              {projectCollabTools.map((tool) => (
                <li
                  key={tool}
                  className="checkbox"
                  style={{
                    breakInside: "avoid",
                    pageBreakInside: "avoid",
                    fontSize: "0.875rem",
                    verticalAlign: "top",
                  }}
                >
                  <input
                    type="checkbox"
                    id={`projectCollabTools-desktop-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData?.projectCollabTools as string[]) || []
                    ).includes(tool)}
                    onChange={(e) =>
                      handleSkillsChange(
                        "projectCollabTools",
                        tool,
                        e.target.checked
                      )
                    }
                  />
                  <label
                    htmlFor={`projectCollabTools-desktop-${tool.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(tool)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other project collaboration tools input */}
          {isOtherSelected("projectCollabTools") && (
            <div className="mt-4">
              <label
                htmlFor="projectCollabToolsOther-desktop"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q36other}
              </label>
              <input
                type="text"
                id="projectCollabToolsOther-desktop"
                className="w-full border border-gray-950 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Slack, Asana, Monday.com..."
                value={data.enhancedData?.projectCollabTools_other || ""}
                onChange={(e) =>
                  handleOtherInputChange("projectCollabTools", e.target.value)
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("projectCollabTools")}
          />
        </div>

        {/* Question 37: Project Management Methodologies */}
        <div>
          <div className="block text-lg font-semibold text-gray-900 mb-2">
            37. {t.q37}
          </div>
          <fieldset className="gc-chckbxrdio">
            <legend className="wb-inv">37. {t.q37}</legend>
            <ul className="list-unstyled lst-spcd-2 colcount-md-2 colcount-sm-2">
              {projectMgmtMethodologies.map((methodology) => (
                <li
                  key={methodology}
                  className="checkbox"
                  style={{
                    breakInside: "avoid",
                    pageBreakInside: "avoid",
                    fontSize: "0.875rem",
                    verticalAlign: "top",
                  }}
                >
                  <input
                    type="checkbox"
                    id={`projectMgmtMethodologies-desktop-${methodology.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    checked={(
                      (data.enhancedData
                        ?.projectMgmtMethodologies as string[]) || []
                    ).includes(methodology)}
                    onChange={(e) =>
                      handleSkillsChange(
                        "projectMgmtMethodologies",
                        methodology,
                        e.target.checked
                      )
                    }
                  />
                  <label
                    htmlFor={`projectMgmtMethodologies-desktop-${methodology.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  >
                    {getTranslatedSkillLabel(methodology)}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {/* Other project management methodologies input */}
          {isOtherSelected("projectMgmtMethodologies") && (
            <div className="mt-4">
              <label
                htmlFor="projectMgmtMethodologiesOther-desktop"
                className="block text-lg font-semibold text-gray-900 mb-2"
              >
                {t.q37other}
              </label>
              <input
                type="text"
                id="projectMgmtMethodologiesOther-desktop"
                className="w-full border border-gray-950 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., DevOps, Design Thinking, Feature-Driven Development..."
                value={data.enhancedData?.projectMgmtMethodologies_other || ""}
                onChange={(e) =>
                  handleOtherInputChange(
                    "projectMgmtMethodologies",
                    e.target.value
                  )
                }
              />
            </div>
          )}
          <ExtractedFieldIndicator
            isExtracted={isFieldExtracted("projectMgmtMethodologies")}
          />
        </div>

        {/* Questions 38 and so on: Resume-related questions */}

        {/* Question 38: Additional Information */}
        <div>
          <label
            htmlFor="additionalInformation"
            className="block text-lg font-semibold text-gray-900 mb-2"
          >
            38. {t.q40} <span className="text-xs text-gray-500"></span>
          </label>
          <p className="text-lg text-gray-900 mb-3">{t.additionalInfoDesc}</p>
          <textarea
            id="additionalInformation"
            rows={4}
            maxLength={500}
            className={`w-full border p-3 focus:ring-2 focus:ring-blue-500 ${
              hasUrlError
                ? "border-red-500 focus:border-red-500 bg-red-50"
                : charCount >= 450
                  ? "border-yellow-500 focus:border-yellow-500 bg-yellow-50"
                  : "border-gray-950 focus:border-blue-500"
            }`}
            placeholder={t.q38Placeholder}
            value={data.enhancedData?.additionalInformation || ""}
            onChange={(e) => {
              const text = e.target.value;
              e.currentTarget.setCustomValidity(
                detectUrls(text) ? t.formValidationUrlMessage : ""
              );
              setCharCount(text.length);
              if (text.length <= 500) {
                onUpdate({
                  enhancedData: {
                    ...data.enhancedData,
                    additionalInformation: text,
                  },
                });
              }
              validateAdditionalInfo(text);
            }}
          />
          {/* Character counter */}
          <div className="flex justify-between mt-1">
            <div>
              {hasUrlError && (
                <p className="text-xs text-red-600">
                  {t.formValidationUrlMessage}
                </p>
              )}
            </div>
            <div
              className={`text-xs ${
                charCount >= 450
                  ? charCount >= 480
                    ? "text-red-600"
                    : "text-yellow-600"
                  : "text-gray-500"
              }`}
            >
              {charCount}/500
            </div>
          </div>
        </div>
      </div>
      {/* Navigation */}
        <div className="flex justify-between pt-6">
          <button
            type="button"
            onClick={onPrevious}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t.previousButton}
          </button>
          <button
            type="submit"
            className="px-6 py-3 rounded-lg font-medium transition-colors primary-action-button"
          >
            {t.continueReview}
          </button>
        </div>
      </motion.form>
    );
};

export default ITCompetenciesStep;
